import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CalendarClock, Flame, IndianRupee, MapPinned, TrendingUp, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import CrmShell from "@/components/crm/CrmShell";
import LeadDrawer from "@/components/crm/LeadDrawer";
import { Panel, money, when } from "@/components/crm/ui";
import { STAGES, corridorLabel, stageLabel } from "@/config/brand";
import { useAuth } from "@/context/AuthContext";

const KPI = ({ icon: Icon, label, value, hint, testid }) => (
  <Panel className="relative overflow-hidden" data-testid={testid}>
    <div className="flex items-start justify-between">
      <span className="text-xs font-bold uppercase tracking-wide text-muted2">{label}</span>
      <Icon className="h-4 w-4 text-brandDark" />
    </div>
    <div className="mt-3 font-display text-2xl font-semibold text-ink lg:text-3xl">{value}</div>
    {hint && <div className="mt-1 text-[11px] text-muted2">{hint}</div>}
  </Panel>
);

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [employees, setEmployees] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/followups", { params: { window: "week" } }),
      ]);
      setStats(s.data);
      setFollowups(f.data);
    } catch (e) {
      toast.error(errText(e));
    }
  }, []);

  useEffect(() => {
    document.title = "BRICKSPACE CRM | Dashboard";
    load();
    api.get("/users").then(({ data }) => setEmployees(data.filter((u) => u.role === "employee")));
  }, [load]);

  useEffect(() => {
    const poll = setInterval(load, 45000);
    return () => clearInterval(poll);
  }, [load]);

  const stageData = stats
    ? STAGES.map((s) => ({ name: s.label, value: stats.by_status[s.key] || 0, fill: s.accent }))
    : [];

  return (
    <CrmShell
      title={isAdmin ? "Owner dashboard" : `Hello, ${user?.name?.split(" ")[0]}`}
      subtitle={isAdmin ? "Live lead performance across the NH-24 corridor" : "Your lead queue and today's actions"}
      actions={
        <Link
          to="/crm/pipeline"
          data-testid="dashboard-open-pipeline"
          className="rounded-full border border-brand/40 bg-mint px-4 py-2 text-xs font-semibold text-brand"
        >
          Open pipeline
        </Link>
      }
    >
      {!stats ? (
        <p className="text-sm text-body">Loading…</p>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KPI icon={Users} label="Total leads" value={stats.total_leads} hint={`${stats.new_week} in last 7 days`} testid="kpi-total-leads" />
            <KPI icon={UserPlus} label="New today" value={stats.new_today} hint={isAdmin ? `${stats.unassigned} unassigned` : "keep calling"} testid="kpi-new-today" />
            <KPI icon={TrendingUp} label="Conversion" value={`${stats.conversion_rate}%`} hint={`${stats.closed} bookings closed`} testid="kpi-conversion" />
            <KPI icon={IndianRupee} label="Open pipeline" value={money(stats.pipeline_value)} hint={`${money(stats.closed_value)} closed`} testid="kpi-pipeline-value" />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Panel className="lg:col-span-2" data-testid="lead-trend-panel">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-muted2">
                  Lead flow · last 14 days
                </span>
              </div>
              <div className="mt-4 h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.trend}>
                    <CartesianGrid stroke="#EFF0F1" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#6b7280", fontSize: 10 }} axisLine={false} tickLine={false} width={24} />
                    <Tooltip
                      contentStyle={{ background: "#FFFFFF", border: "1px solid #E4E5E7", borderRadius: 10, fontSize: 12 }}
                    />
                    <Line type="monotone" dataKey="leads" stroke="#1DBF73" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel data-testid="stage-breakdown-panel">
              <span className="text-xs font-bold uppercase tracking-wide text-muted2">
                Pipeline by stage
              </span>
              <div className="mt-4 h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stageData} layout="vertical" margin={{ left: 10 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" tick={{ fill: "#62646A", fontSize: 10 }} width={104} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ background: "#FFFFFF", border: "1px solid #E4E5E7", borderRadius: 10, fontSize: 12 }}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={12}>
                      {stageData.map((d, i) => (
                        <Cell key={i} fill={d.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Panel data-testid="today-actions-panel">
              <span className="text-xs font-bold uppercase tracking-wide text-muted2">
                Today&apos;s actions
              </span>
              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-3 rounded-xl border border-line bg-cloud p-3">
                  <CalendarClock className="h-4 w-4 text-brand" />
                  <div>
                    <div className="text-sm font-semibold">{stats.followups_today} follow-ups due</div>
                    <Link to="/crm/schedule" className="text-[11px] text-brandDark hover:text-brand">
                      View schedule
                    </Link>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-line bg-cloud p-3">
                  <MapPinned className="h-4 w-4 text-brand" />
                  <div>
                    <div className="text-sm font-semibold">{stats.visits_upcoming} site visits upcoming</div>
                    <span className="text-[11px] text-muted2">Chauffeur pickups to confirm</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-line bg-cloud p-3">
                  <Flame className="h-4 w-4 text-red-500" />
                  <div>
                    <div className="text-sm font-semibold">{stats.by_temperature?.hot || 0} hot leads</div>
                    <Link to="/crm/leads" className="text-[11px] text-brandDark hover:text-brand">
                      Call them first
                    </Link>
                  </div>
                </div>
              </div>
            </Panel>

            <Panel className="lg:col-span-2" data-testid="upcoming-followups-panel">
              <span className="text-xs font-bold uppercase tracking-wide text-muted2">
                Next follow-ups
              </span>
              <div className="mt-4 divide-y divide-line">
                {followups.length === 0 && <p className="text-xs text-muted2">Nothing pending this week.</p>}
                {followups.slice(0, 6).map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setOpenId(f.lead_id)}
                    data-testid={`followup-row-${f.id}`}
                    className="flex w-full items-center justify-between py-3 text-left hover:text-brand"
                  >
                    <div>
                      <div className="text-sm font-medium">{f.lead_name}</div>
                      <div className="mt-0.5 text-[11px] text-muted2">
                        {f.channel} · {f.user_name}
                      </div>
                    </div>
                    <span className="text-[11px] text-brandDark">{when(f.due_at)}</span>
                  </button>
                ))}
              </div>
            </Panel>
          </div>

          {isAdmin && (
            <Panel data-testid="team-performance-panel">
              <span className="text-xs font-bold uppercase tracking-wide text-muted2">
                Advisor performance
              </span>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase tracking-wide text-muted2">
                      <th className="pb-3">Advisor</th>
                      <th className="pb-3">Leads</th>
                      <th className="pb-3">Closed</th>
                      <th className="pb-3">Conversion</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {stats.team.map((t) => (
                      <tr key={t.id}>
                        <td className="py-3">{t.name}</td>
                        <td className="py-3 text-body">{t.leads}</td>
                        <td className="py-3 text-body">{t.closed}</td>
                        <td className="py-3 text-brand">{t.conversion}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {Object.entries(stats.by_corridor).map(([k, v]) => (
              <Panel key={k} className="py-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted2">{corridorLabel(k)}</div>
                <div className="mt-1.5 font-display text-xl font-semibold">{v}</div>
                <div className="text-[11px] text-muted2">leads</div>
              </Panel>
            ))}
          </div>
        </div>
      )}

      <LeadDrawer
        leadId={openId}
        open={!!openId}
        onClose={() => setOpenId(null)}
        employees={employees}
        onChanged={load}
      />
    </CrmShell>
  );
}
